import React, { useState, useEffect } from 'react';
import { Card, Typography, Row, Col, Button, List, Avatar, Tag, Rate, Form, Input, Select, InputNumber, Switch, message, Modal, DatePicker, Popover } from 'antd';
import { TeamOutlined, UserOutlined, StarOutlined, SettingOutlined, MessageOutlined } from '@ant-design/icons';
import api from '../../services/api';
import { absoluteUploadUrl } from '../../utils/uploadUrl';
import CepInput from '../../components/CepInput';
import TeamSlugCard from '../../components/community/TeamSlugCard';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;

const TeamCommunity = () => {
  const [teamProfile, setTeamProfile] = useState(null);
  const [nearbyPlayers, setNearbyPlayers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [pendingRatings, setPendingRatings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [profileForm] = Form.useForm();
  const [inviteModal, setInviteModal] = useState({ visible: false, player: null });
  const [ratingModal, setRatingModal] = useState({ visible: false, game: null });
  const isMobile = window.innerWidth <= 768;

  const skillLevels = [
    { value: 'iniciante', label: 'Iniciante' },
    { value: 'recreativo', label: 'Recreativo' },
    { value: 'intermediario', label: 'Intermediário' },
    { value: 'avancado', label: 'Avançado' },
    { value: 'semi_pro', label: 'Semi-pro' }
  ];

  useEffect(() => {
    loadTeamProfile();
    loadNearbyPlayers();
    loadInvites();
    loadPendingRatings();
  }, []);

  const loadTeamProfile = async () => {
    try {
      const response = await api.get('/community/teams/profile', {
        // Sem headers hardcoded: interceptor do axios ja monta Authorization
        // e x-active-customer-id atualizados no momento da request.
      });
      const profileData = { ...response.data };

      // Converter data de fundação para dayjs se existir
      if (profileData.founded_date) {
        try {
          profileData.founded_date = dayjs(profileData.founded_date);
        } catch (dateError) {
          console.warn('Erro ao converter data:', dateError);
          profileData.founded_date = null;
        }
      }

      setTeamProfile(response.data);
      profileForm.setFieldsValue(profileData);
    } catch (error) {
      console.error('Erro ao carregar perfil:', error);
      if (error.response?.status === 404) {
        // Time ainda não tem perfil na comunidade
        setTeamProfile({ exists: false });
      } else {
        message.error('Erro ao carregar perfil do time');
      }
    }
  };

  const loadNearbyPlayers = async () => {
    try {
      const response = await api.get('/community/teams/nearby-players', {
        // Sem headers hardcoded: interceptor do axios ja monta Authorization
        // e x-active-customer-id atualizados no momento da request.
      });
      setNearbyPlayers(response.data.players);
    } catch (error) {
      console.error('Erro ao carregar jogadores:', error);
    }
  };

  const loadInvites = async () => {
    try {
      const response = await api.get('/community/invites/team', {
        // Sem headers hardcoded: interceptor do axios ja monta Authorization
        // e x-active-customer-id atualizados no momento da request.
      });
      setInvites(response.data);
    } catch (error) {
      console.error('Erro ao carregar convites:', error);
    }
  };

  const loadPendingRatings = async () => {
    try {
      const response = await api.get('/community/ratings/pending', {
        // Sem headers hardcoded: interceptor do axios ja monta Authorization
        // e x-active-customer-id atualizados no momento da request.
      });
      setPendingRatings(response.data);
    } catch (error) {
      console.error('Erro ao carregar avaliações:', error);
    }
  };

  const handleCreateProfile = async (values) => {
    setLoading(true);
    try {
      const formData = { ...values };

      // Converter data de fundação para string se existir
      if (formData.founded_date) {
        formData.founded_date = formData.founded_date.format('YYYY-MM-DD');
      }

      const response = await api.post('/community/teams/profile', formData, {
        // Sem headers hardcoded: interceptor do axios ja monta Authorization
        // e x-active-customer-id atualizados no momento da request.
      });

      setTeamProfile(response.data);
      message.success('Perfil criado na comunidade!');
      setTimeout(() => {
        window.location.href = '/community/team';
      }, 1500);
    } catch (error) {
      message.error('Erro ao criar perfil');
    }
    setLoading(false);
  };

  const handleUpdateProfile = async (values) => {
    setLoading(true);
    try {
      const formData = { ...values };

      // Converter data de fundação para string se existir
      if (formData.founded_date) {
        formData.founded_date = formData.founded_date.format('YYYY-MM-DD');
      }

      const response = await api.put('/community/teams/profile', formData, {
        // Sem headers hardcoded: interceptor do axios ja monta Authorization
        // e x-active-customer-id atualizados no momento da request.
      });

      setTeamProfile(response.data);
      message.success('Perfil atualizado!');
      setTimeout(() => {
        window.location.href = '/community/team';
      }, 1500);
    } catch (error) {
      message.error('Erro ao atualizar perfil');
    }
    setLoading(false);
  };

  const handleInvitePlayer = async (values) => {
    setLoading(true);
    try {
      await api.post('/community/invites/invite-player', {
        player_id: inviteModal.player.id,
        message: values.message,
        game_date: values.game_date.format('YYYY-MM-DD'),
        game_time: values.game_time
      }, {
        // Sem headers hardcoded: interceptor do axios ja monta Authorization
        // e x-active-customer-id atualizados no momento da request.
      });

      message.success('Convite enviado!');
      setInviteModal({ visible: false, player: null });
      loadInvites();
    } catch (error) {
      message.error('Erro ao enviar convite');
    }
    setLoading(false);
  };

  const handleRespondApplication = async (inviteId, status) => {
    try {
      await api.put(`/community/invites/${inviteId}/respond`, { status }, {
        // Sem headers hardcoded: interceptor do axios ja monta Authorization
        // e x-active-customer-id atualizados no momento da request.
      });

      message.success(status === 'ACCEPTED' ? 'Candidatura aceita!' : 'Candidatura recusada');
      loadInvites();
    } catch (error) {
      message.error('Erro ao responder candidatura');
    }
  };

  const handleRatePlayer = async (values) => {
    setLoading(true);
    try {
      await api.post('/community/ratings/rate-player', {
        player_id: ratingModal.game.player_id,
        game_date: ratingModal.game.game_date,
        technical_skill: values.technical_skill,
        behavior: values.behavior,
        punctuality: values.punctuality,
        teamwork: values.teamwork,
        comments: values.comments
      }, {
        // Sem headers hardcoded: interceptor do axios ja monta Authorization
        // e x-active-customer-id atualizados no momento da request.
      });

      message.success('Avaliação enviada!');
      setRatingModal({ visible: false, game: null });
      loadPendingRatings();
    } catch (error) {
      message.error('Erro ao enviar avaliação');
    }
    setLoading(false);
  };

  const getSkillColor = (level) => {
    const colors = {
      iniciante: 'green',
      recreativo: 'blue',
      intermediario: 'orange',
      avancado: 'red',
      semi_pro: 'purple'
    };
    return colors[level] || 'default';
  };

  // Se não tem perfil, mostrar formulário de criação
  if (teamProfile && teamProfile.exists === false) {
    return (
      <div style={{ padding: '2px' }}>
        <Card>
          <Title level={isMobile ? 3 : 2}>Criar Perfil na Comunidade</Title>
          <Text>Crie o perfil do seu time para encontrar jogadores!</Text>

          <Form
            form={profileForm}
            layout="vertical"
            onFinish={handleCreateProfile}
            style={{ marginTop: 24 }}
          >
            <Row gutter={24}>
              <Col xs={24} md={12}>
                <Form.Item name="court_name" label="Nome da Quadra" rules={[{ required: true }]}>
                  <Input placeholder="Nome da quadra/campo" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="skill_level" label="Nível Técnico" rules={[{ required: true }]}>
                  <Select placeholder="Selecione o nível">
                    {skillLevels.map(level => (
                      <Option key={level.value} value={level.value}>{level.label}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col xs={24} md={8}>
                <Form.Item name="guest_price" label="Valor Avulso (R$)">
                  <InputNumber style={{ width: '100%' }} placeholder="0.00" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="monthly_price" label="Valor Mensalista (R$)">
                  <InputNumber style={{ width: '100%' }} placeholder="0.00" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="search_radius_km" label="Raio de Busca (km)">
                  <InputNumber min={1} max={50} style={{ width: '100%' }} placeholder="15" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col xs={24} md={12} style={{ whiteSpace: "nowrap" }}>
                <Form.Item name="cep" label="CEP" rules={[{ required: true }]} style={{ paddingRight: 4 }}>
                  <CepInput
                    value={profileForm.getFieldValue('cep')}
                    onChange={(value) => profileForm.setFieldsValue({ cep: value })}
                    onAddressChange={(addressData) => {
                      console.log('Dados recebidos do CEP:', addressData);
                      profileForm.setFieldsValue({
                        address: addressData.address,
                        city: addressData.city,
                        state: addressData.state,
                        neighborhood: addressData.neighborhood,
                        latitude: addressData.latitude,
                        longitude: addressData.longitude
                      });
                      console.log('Valores definidos no form:', profileForm.getFieldsValue());
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="address" label="Endereço">
                  <Input placeholder="Será preenchido automaticamente" disabled />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col xs={24} md={8}>
                <Form.Item name="city" label="Cidade">
                  <Input placeholder="São Paulo" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="state" label="Estado">
                  <Input placeholder="SP" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="neighborhood" label="Bairro">
                  <Input placeholder="Vila Madalena" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col xs={24} md={12}>
                <Form.Item name="latitude" label="Latitude">
                  <InputNumber style={{ width: '100%' }} precision={8} disabled />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="longitude" label="Longitude">
                  <InputNumber style={{ width: '100%' }} precision={8} disabled />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="description" label="Descrição do Time">
              <Input.TextArea rows={3} placeholder="Conte sobre o time, horários, local dos jogos..." />
            </Form.Item>

            <Form.Item name="conduct_code" label="Código de Conduta">
              <Input.TextArea rows={2} placeholder="Regras de comportamento do time" />
            </Form.Item>

            <Row gutter={24}>
              <Col xs={24} md={12}>
                <Form.Item name="is_public" label="Perfil Público" valuePropName="checked">
                  <Switch checkedChildren="Público" unCheckedChildren="Privado" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="founded_date" label="Data de Fundação">
                  <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" placeholder="Selecione a data" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading} size="large" block>
                Criar Perfil na Comunidade
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </div>
    );
  }

  if (!teamProfile) return <div>Carregando...</div>;

  return (
    <div style={{ padding: '20px' }}>
      <Row gutter={[16, 16]}>
        {/* Perfil do Time */}
        <Col xs={24} lg={8}>
          <Card title={<><SettingOutlined /> Perfil do Time</>}>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <Avatar size={80} src={absoluteUploadUrl(teamProfile.logo_url)} icon={<TeamOutlined />} />
              <Title level={4} style={{ margin: '8px 0' }}>{teamProfile.name}</Title>
              <Tag color={getSkillColor(teamProfile.skill_level)}>{teamProfile.skill_level}</Tag>
              {teamProfile.average_rating > 0 && (
                <div style={{ marginTop: 8 }}>
                  <Rate disabled value={teamProfile.average_rating} />
                  <Text type="secondary"> ({teamProfile.total_ratings} avaliações)</Text>
                </div>
              )}
            </div>

            <div style={{ marginBottom: 16 }}>
              <Text strong>Público: </Text>
              <Tag color={teamProfile.is_public ? 'green' : 'red'}>
                {teamProfile.is_public ? 'Sim' : 'Não'}
              </Tag>
            </div>

            {/* Link publico + editor de slug */}
            <TeamSlugCard
              currentSlug={teamProfile.slug}
              updateEndpoint="/community/teams/profile"
              onSaved={(s) => setTeamProfile(prev => ({ ...prev, slug: s }))}
            />

            <Button type="primary" block onClick={() => window.location.href = '/community/team/edit'}>
              Editar Perfil
            </Button>
          </Card>
        </Col>

        {/* Jogadores Próximos */}
        <Col xs={24} lg={8}>
          <Card title={<><UserOutlined /> Jogadores Próximos</>}>
            <List
              dataSource={nearbyPlayers.slice(0, 5)}
              renderItem={player => (
                <List.Item
                  actions={[
                    <Button
                      size="small"
                      type="primary"
                      onClick={() => setInviteModal({ visible: true, player })}
                    >
                      Convidar
                    </Button>
                  ]}
                >
                  <List.Item.Meta
                    avatar={<Avatar src={player.profile_photo} icon={<UserOutlined />} />}
                    title={player.name}
                    description={
                      <div>
                        <div>{player.preferred_position} - {player.neighborhood}</div>
                        <Tag color={getSkillColor(player.skill_level)}>{player.skill_level}</Tag>
                        {player.average_rating > 0 && <Rate disabled value={player.average_rating} />}
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
            <Button type="link" block onClick={() => window.location.href = '/community/map'}>
              Ver Todos no Mapa
            </Button>
          </Card>
        </Col>

        {/* Convites e Avaliações */}
        <Col xs={24} lg={8}>
          <Card title="Candidaturas Recebidas" style={{ marginBottom: 16 }}>
            <List
              dataSource={invites.filter(i => !i.invited_by_team).slice(0, 3)}
              renderItem={invite => (
                <List.Item
                  actions={[
                    invite.message && (
                      <Popover
                        content={<div style={{ maxWidth: 200 }}>{invite.message}</div>}
                        title="Mensagem do jogador"
                        trigger="click"
                      >
                        <Button size="small" icon={<MessageOutlined />} />
                      </Popover>
                    ),
                    <Button size="small" type="primary" onClick={() => handleRespondApplication(invite.id, 'ACCEPTED')}>
                      Aceitar
                    </Button>,
                    <Button size="small" onClick={() => handleRespondApplication(invite.id, 'REJECTED')}>
                      Recusar
                    </Button>
                  ].filter(Boolean)}
                >
                  <List.Item.Meta
                    title={invite.player_name}
                    description={[
                      invite.preferred_position,
                      invite.game_date ? dayjs(invite.game_date).format('DD/MM/YYYY') : null,
                    ].filter(Boolean).join(' · ')}
                  />
                </List.Item>
              )}
            />
            <Button type="link" block onClick={() => window.location.href = '/community/invites/history'}>
              Ver Histórico Completo
            </Button>
          </Card>

          <Card title={<><StarOutlined /> Avaliações Pendentes</>}>
            <List
              dataSource={pendingRatings.slice(0, 3)}
              renderItem={game => (
                <List.Item
                  actions={[
                    <Button
                      size="small"
                      type="primary"
                      onClick={() => setRatingModal({ visible: true, game })}
                    >
                      Avaliar
                    </Button>
                  ]}
                >
                  <List.Item.Meta
                    title={game.player_name}
                    description={`Jogo de ${game.game_date}`}
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>
      </Row>

      {/* Modal de Convite */}
      <Modal
        title={`Convidar ${inviteModal.player?.name}`}
        open={inviteModal.visible}
        onCancel={() => setInviteModal({ visible: false, player: null })}
        footer={null}
      >
        <Form onFinish={handleInvitePlayer} layout="vertical">
          <Form.Item name="game_date" label="Data do Jogo" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="game_time" label="Horário">
            <Input placeholder="19:00" />
          </Form.Item>
          <Form.Item name="message" label="Mensagem">
            <Input.TextArea rows={3} placeholder="Convite para jogar..." />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block>
              Enviar Convite
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal de Avaliação */}
      <Modal
        title={`Avaliar ${ratingModal.game?.player_name}`}
        open={ratingModal.visible}
        onCancel={() => setRatingModal({ visible: false, game: null })}
        footer={null}
      >
        <Form onFinish={handleRatePlayer} layout="vertical">
          <Form.Item name="technical_skill" label="Habilidade Técnica" rules={[{ required: true }]}>
            <Rate />
          </Form.Item>
          <Form.Item name="behavior" label="Comportamento" rules={[{ required: true }]}>
            <Rate />
          </Form.Item>
          <Form.Item name="punctuality" label="Pontualidade" rules={[{ required: true }]}>
            <Rate />
          </Form.Item>
          <Form.Item name="teamwork" label="Trabalho em Equipe" rules={[{ required: true }]}>
            <Rate />
          </Form.Item>
          <Form.Item name="comments" label="Comentários">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block>
              Enviar Avaliação
            </Button>
          </Form.Item>
        </Form>
      </Modal>


    </div>
  );
};

export default TeamCommunity;